import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import Peer from 'simple-peer';
import { BASE_SOCKET_URL } from '../../redux/actions/ip';
import VideoSection from '../GroupVideoCallScreen/VideoSection/VideoSection';
import Header from '../GroupVideoCallScreen/Header/Header';
import Footer from '../GroupVideoCallScreen/Footer/Footer';
import { ICE_SERVERS } from '../../utils/iceConfig';

const GuestVideoScreen = () => {
  const { t } = useTranslation();
  const { roomId } = useParams();

  // Lobby state
  const [guestName, setGuestName] = useState('');
  const [nameError, setNameError] = useState('');
  const [callStarted, setCallStarted] = useState(false);

  // Call state
  const [peers, setPeers] = useState([]);
  const [localStream, setLocalStream] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [callSeconds, setCallSeconds] = useState(0);
  const [mediaError, setMediaError] = useState(null);
  const [left, setLeft] = useState(false);

  const socketRef = useRef();
  const peersRef = useRef([]);
  const localStreamRef = useRef();

  const handleJoin = () => {
    if (!guestName.trim()) { setNameError(t('screens.video.guest.nameError')); return; }
    setCallStarted(true);
  };

  // Timer
  useEffect(() => {
    if (!callStarted) return;
    const interval = setInterval(() => setCallSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [callStarted]);

  // Media + socket setup
  useEffect(() => {
    if (!callStarted || !roomId) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaError(t('screens.video.guest.browserUnsupported'));
      return;
    }

    navigator.mediaDevices.getUserMedia({ video: true, audio: true }).then(stream => {
      setLocalStream(stream);
      localStreamRef.current = stream;

      socketRef.current = io.connect(BASE_SOCKET_URL, {
        withCredentials: false,
        query: {
          isGuest: 'true',
          guestName: guestName.trim(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      socketRef.current.emit('join room', roomId);

      socketRef.current.on('all users', users => {
        const newPeers = [];
        users.forEach(userID => {
          // Guest is joining existing call — don't initiate
          // Create peer with initiator:false and wait for host's offer
          const peer = new Peer({ initiator: false, trickle: false, stream, config: ICE_SERVERS });
          peer.on('signal', signal => {
            socketRef.current.emit('returning signal', { signal, callerID: userID });
          });
          peersRef.current.push({ peerID: userID, peer, name: '' });
          newPeers.push({ peer, name: '' });
        });
        setPeers(newPeers);
      });

      socketRef.current.on('user joined', payload => {
        const peer = addPeer(payload.signal, payload.callerID, stream);
        const name = payload.callerName || '';
        peersRef.current.push({ peerID: payload.callerID, peer, name });
        setPeers(prev => [...prev, { peer, name }]);
      });

      socketRef.current.on('receiving returned signal', payload => {
        const item = peersRef.current.find(p => p.peerID === payload.id);
        if (item) item.peer.signal(payload.signal);
      });

      socketRef.current.on('user left', userID => {
        const peerObj = peersRef.current.find(p => p.peerID === userID);
        if (peerObj) peerObj.peer.destroy();
        peersRef.current = peersRef.current.filter(p => p.peerID !== userID);
        setPeers(peersRef.current.map(p => ({ peer: p.peer, name: p.name })));
      });
    }).catch(err => {
      setMediaError(err.name === 'NotAllowedError'
        ? t('screens.video.guest.permissionDenied')
        : t('screens.video.guest.mediaGenericError'));
    });

    return () => {
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
      peersRef.current.forEach(({ peer }) => { try { peer.destroy(); } catch (_) {} });
      if (socketRef.current) {
        socketRef.current.emit('user leaving', { roomId });
        socketRef.current.disconnect();
      }
    };
  }, [callStarted, roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPeer = (userToSignal, callerID, stream) => {
    const peer = new Peer({ initiator: true, trickle: false, stream, config: ICE_SERVERS });
    peer.on('signal', signal => {
      socketRef.current.emit('sending signal', { userToSignal, callerID, signal, callerName: guestName.trim() });
    });
    return peer;
  };

  const addPeer = (incomingSignal, callerID, stream) => {
    const peer = new Peer({ initiator: false, trickle: false, stream, config: ICE_SERVERS });
    peer.on('signal', signal => {
      socketRef.current.emit('returning signal', { signal, callerID });
    });
    peer.signal(incomingSignal);
    return peer;
  };

  const handleToggleMic = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
    }
  };

  const handleToggleVideo = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) { track.enabled = !track.enabled; setVideoOn(track.enabled); }
    }
  };

  const handleLeaveCall = () => {
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
    peersRef.current.forEach(({ peer }) => { try { peer.destroy(); } catch (_) {} });
    if (socketRef.current) {
      socketRef.current.emit('user leaving', { roomId });
      socketRef.current.disconnect();
    }
    setLeft(true);
  };

  // ── Left screen ───────────────────────────────────────────
  if (left) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>👋</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>{t('screens.video.guest.leftTitle')}</div>
          <div style={{ fontSize: 14, color: '#64748b' }}>{t('screens.video.guest.leftSub')}</div>
        </div>
      </div>
    );
  }

  // ── Media error ───────────────────────────────────────────
  if (mediaError) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '32px 40px', maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📷</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>{t('screens.video.guest.unableTitle')}</div>
          <div style={{ fontSize: 14, color: '#b91c1c', lineHeight: 1.6 }}>{mediaError}</div>
        </div>
      </div>
    );
  }

  // ── Lobby / name entry ────────────────────────────────────
  if (!callStarted) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: '24px 16px', boxSizing: 'border-box' }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 'clamp(28px, 6vw, 48px) clamp(20px, 8vw, 56px)', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
          <img src="/Logo2.svg" alt="Regure" style={{ height: 30, marginBottom: 28, opacity: 0.85 }} />
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>{t('screens.video.guest.invitedTitle')}</div>
          <div style={{ fontSize: 14, color: '#64748b', marginBottom: 32, lineHeight: 1.6 }}>{t('screens.video.guest.invitedSub')}</div>
          <input
            autoFocus
            placeholder={t('screens.video.guest.yourName')}
            value={guestName}
            onChange={e => { setGuestName(e.target.value); setNameError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleJoin(); }}
            style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${nameError ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 9, fontSize: 15, outline: 'none', boxSizing: 'border-box', marginBottom: nameError ? 6 : 20, fontFamily: 'inherit', color: '#1e293b' }}
          />
          {nameError && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 16, textAlign: 'left' }}>{nameError}</div>}
          <button
            onClick={handleJoin}
            style={{ width: '100%', padding: '13px 0', background: '#800020', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
          >
            {t('screens.video.guest.joinMeeting')}
          </button>
          <div style={{ marginTop: 20, fontSize: 12, color: '#94a3b8' }}>{t('screens.video.guest.noAccountNeeded')}</div>
        </div>
      </div>
    );
  }

  // ── Active call ───────────────────────────────────────────
  const peerNames = peers.map(p => p.name);
  const peerObjects = peers.map(p => p.peer);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <Header headerTitle={t('screens.video.guest.headerTitle')} callSeconds={callSeconds} participantCount={peers.length + 1} />
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <VideoSection rightPanelOpen={false} peers={peerObjects} peerNames={peerNames} localStream={localStream} videoOn={videoOn} />
        {peers.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(6,13,26,0.72)', zIndex: 10 }}>
            <div style={{ textAlign: 'center', color: '#f1f5f9' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{t('screens.video.guest.waitingTitle')}</div>
              <div style={{ fontSize: 14, color: '#94a3b8' }}>{t('screens.video.guest.waitingSub')}</div>
            </div>
          </div>
        )}
      </div>
      <Footer
        onLeaveCall={handleLeaveCall}
        onToggleMic={handleToggleMic}
        onToggleVideo={handleToggleVideo}
        micOn={micOn}
        videoOn={videoOn}
        screenShare={false}
        onAddParticipants={() => {}}
        onScreenShare={() => {}}
        onPanelTabClick={() => {}}
        rightPanelOpen={false}
        rightPanelTab=""
      />
    </div>
  );
};

export default GuestVideoScreen;
